<template>
  <div>
    <div>
      <div class="flex items-center gap-x-2">
        <p class="truncate text-sm font-medium text-gray-500">Nouveaux contrats</p>
        <VTooltip theme="info-tooltip">
          <InformationCircleIcon class="cursor-pointer h-5 w-4 text-gray-400" aria-hidden="true" />
          <template #popper>
            <span>Nombre de nouveaux contrats vendus durant la période sélectionnée.</span>
          </template>
        </VTooltip>
      </div>
      <p class="text-lg pl-0.5 mt-1 tracking-tight text-gray-700">
        {{ periodContracts?.current }}
        <span v-if="periodContracts?.previous != null" class="text-sm ml-4 text-gray-500">
          {{ periodContracts.previous }} {{ comparisonType ? $t(comparisonType) : "" }}
        </span>
        <Variation class="ml-2" :variation="periodContracts?.variation" />
      </p>
    </div>

    <LineChart v-if="isDailyView" :data="dailyChartData" :options="chartOptions" :chart-height="170" />
    <LineChart v-else :data="monthlyChartData" :options="chartOptions" :chart-height="170" />
  </div>
</template>

<script>
import { LineChart, Variation } from "@estaly/ui";
import { InformationCircleIcon } from "@heroicons/vue/outline";

export default {
  components: { LineChart, Variation, InformationCircleIcon },

  props: {
    analytics: { type: Object, required: true },
    chartView: { type: String, required: true },
    comparisonType: { type: String, default: "previousMonth" },
  },

  computed: {
    isDailyView() {
      return this.chartView === "daily";
    },

    periodContracts() {
      return this.analytics?.general?.periodContracts;
    },

    dailyChartData() {
      const daily = this.analytics?.sales?.dailyPlanSales;
      if (!daily?.current) return {};

      const currentData = daily.current.map((entry) => ({ date: entry.date, value: entry.count }));
      let previousData = [];

      if (daily.previous) {
        previousData = currentData.map((entry, index) => ({
          date: entry.date,
          value: daily.previous[index]?.count || 0,
        }));
      }

      return {
        sold: { color: "#8C1EFC", value: currentData },
        "previous contracts sold": { color: "#D1D5DB", value: previousData },
      };
    },

    monthlyChartData() {
      const monthly = this.analytics?.sales?.monthlyPlanSales;
      const currentData = monthly?.current?.map((entry) => ({ date: entry.date, value: entry.count })) || [];

      return {
        sold: { color: "#8C1EFC", value: currentData },
      };
    },

    activeChartData() {
      return this.isDailyView ? this.dailyChartData : this.monthlyChartData;
    },

    maxValue() {
      const datasets = Object.values(this.activeChartData);
      const allValues = datasets.flatMap((d) => (d.value || []).map((p) => p.value));
      return Math.max(1, ...allValues);
    },

    chartOptions() {
      const isDaily = this.isDailyView;
      const data = this.activeChartData;
      const maxVal = isDaily ? this.maxValue : this.maxValue * 1.15;

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            grid: { display: false },
            title: { display: true, text: isDaily ? "Date" : "Mois" },
            ticks: {
              maxRotation: 0,
              minRotation: 0,
              callback: (value, index, values) => {
                const step = isDaily ? 4 : 2;
                if (index === 0 || index === values.length - 1 || index % step === 0) {
                  return data["sold"]?.value[index]?.date || value;
                }
                return "";
              },
            },
          },
          y: {
            grid: { display: false, drawBorder: false },
            beginAtZero: true,
            title: { display: true, text: "Ventes" },
            ticks: {
              maxTicksLimit: 2,
              callback: (value) => {
                if (value === 0) return value;
                if (isDaily && value === this.maxValue) return value;
                if (!isDaily && Math.abs(value - this.maxValue * 1.15) < 1) return this.maxValue;
                return "";
              },
            },
            suggestedMax: maxVal,
            max: maxVal,
          },
        },
        plugins: {
          annotation: false,
          datalabels: false,
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => ` ${tooltipItem.dataset.label} : ${tooltipItem.raw.y.toLocaleString()}`,
            },
          },
        },
      };
    },
  },
};
</script>
